import { useState } from 'react';
import { api, isLiveBotStatus, type AgentSeatState, type MeetingStatus } from '../lib/api.js';

export function AgentSeatPanel(props: {
  meetingId: string;
  status: MeetingStatus;
  seat: AgentSeatState | null | undefined;
  onChanged: () => Promise<void> | void;
}): JSX.Element | null {
  const { meetingId, status, seat, onChanged } = props;
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const live = isLiveBotStatus(status);
  const active = !!seat && !seat.stopped && seat.status !== 'failed' && seat.status !== 'left';

  if (!live && !seat) return null;

  async function run(label: string, fn: () => Promise<unknown>, ok: string): Promise<void> {
    setBusy(label);
    setError(null);
    setNote(null);
    try {
      await fn();
      setNote(ok);
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  const turns = seat?.turns ?? [];

  return (
    <section className="panel" style={{ marginBottom: 16 }}>
      <h2 className="panel__title">AI</h2>
      <p className="panel__subtle">
        RocketFuel project manager. She/her. She speaks when someone says her name, or after the room goes quiet on a question. Mute stops her voice on the next check. Remove sends her out of the call.
      </p>
      {error && <div className="form-error">{error}</div>}
      {note && <div className="form-success">{note}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {live && !active && (
          <button
            type="button"
            className="btn"
            disabled={!!busy}
            onClick={() => void run('invite', () => api.inviteBasecamp(meetingId), 'AI is joining with voice and video.')}
          >
            {busy === 'invite' ? 'Inviting…' : 'Invite AI with voice and video'}
          </button>
        )}
        {active && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={!!busy}
            onClick={() => void run(
              'mute',
              () => api.muteBasecamp(meetingId, !seat.muted),
              seat.muted ? 'AI can speak again.' : 'AI is muted.',
            )}
          >
            {busy === 'mute' ? 'Updating…' : seat.muted ? 'Unmute AI' : 'Mute AI'}
          </button>
        )}
        {active && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={!!busy}
            onClick={() => void run('kick', () => api.kickBasecamp(meetingId), 'AI is leaving the meeting.')}
          >
            {busy === 'kick' ? 'Removing…' : 'Remove AI'}
          </button>
        )}
      </div>
      {active && (
        <p className="panel__subtle" style={{ marginTop: 8 }}>
          {seat.status === 'live' ? 'She is in the meeting.' : 'She is joining.'}
          {seat.muted ? ' Muted.' : ''}
        </p>
      )}
      {turns.length > 0 && (
        <ul className="panel__subtle" style={{ marginTop: 8, paddingLeft: 18 }}>
          {turns.slice(0, 6).map((t) => (
            <li key={t.id}>
              {t.said ? `She said: ${t.said}` : null}
              {t.suggestion ? ` For you: ${t.suggestion}` : null}
              {!t.said && !t.suggestion && t.heard ? `Heard: ${t.heard}` : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
